import { supabase } from './supabase'
import { OrderStatus } from './types'
import { dispatchDeliveryJob } from './dispatch'

// ─── TRACKING MESSAGES ───────────────────────────────────────────

export const TRACKING_MESSAGES: Record<
  OrderStatus,
  { title: string; message: string }
> = {
  new: {
    title: 'Pedido recibido',
    message: 'Hemos recibido tu pedido',
  },
  accepted: {
    title: 'Pedido aceptado',
    message: 'Tu pedido fue aceptado',
  },
  preparing: {
    title: 'En preparación',
    message: 'Tu pedido está en preparación',
  },
  ready: {
    title: 'Pedido listo',
    message: 'Tu pedido está listo',
  },
  searching_driver: {
    title: 'Buscando conductor',
    message: 'Estamos buscando un conductor para tu pedido',
  },
  out_for_delivery: {
    title: 'En camino',
    message: 'Tu pedido va en camino',
  },
  delivered: {
    title: 'Entregado',
    message: '¡Pedido entregado! Gracias por tu compra',
  },
  cancelled: {
    title: 'Cancelado',
    message: 'Tu pedido ha sido cancelado',
  },
}

// ─── HELPER FUNCTIONS ────────────────────────────────────────────

export async function addTrackingEvent(
  orderId: string,
  restaurantId: string,
  status: OrderStatus,
  title?: string,
  message?: string,
) {
  const defaultMsg = TRACKING_MESSAGES[status]
  const { error } = await supabase.from('customer_tracking_events').insert({
    order_id: orderId,
    restaurant_id: restaurantId,
    status,
    title: title || defaultMsg.title,
    message: message || defaultMsg.message,
    visible_to_customer: true,
  })

  if (error) {
    console.error('[FULFILLMENT] Error creating tracking event:', error)
  }
}

export async function addOrderLog(
  orderId: string,
  restaurantId: string,
  eventType: string,
  payload?: any,
) {
  const { error } = await supabase.from('order_logs').insert({
    order_id: orderId,
    restaurant_id: restaurantId,
    event_type: eventType,
    event_payload: payload || null,
  })

  if (error) {
    console.error('[FULFILLMENT] Error creating order log:', error)
  }
}

// ─── CORE ORCHESTRATION ──────────────────────────────────────────

// Delivery job statuses that mean "still looking for a driver"
const DELIVERY_JOB_SEARCHING_STATUSES = ['open']

/**
 * Recalculates order status based on:
 * - All order_items statuses
 * - delivery_type
 * - Existence/status of delivery_job
 *
 * This is the single source of truth for order state transitions.
 */
export async function recalculateOrderStatus(
  orderId: string,
): Promise<{ success: boolean; newStatus?: OrderStatus; error?: string }> {
  try {
    console.log('[FULFILLMENT] ═══════════════════════════════════')
    console.log('[FULFILLMENT] Recalculating order status for:', orderId)

    // 1. Fetch order
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single()

    if (orderError || !order) {
      console.error('[FULFILLMENT] Order not found:', orderError)
      return { success: false, error: 'Order not found' }
    }

    const currentStatus = order.status as OrderStatus
    console.log('[FULFILLMENT] Current status:', currentStatus)
    console.log('[FULFILLMENT] Delivery type:', order.delivery_type)

    // 2. Fetch all order items
    const { data: items, error: itemsError } = await supabase
      .from('order_items')
      .select('*')
      .eq('order_id', orderId)

    if (itemsError) {
      console.error('[FULFILLMENT] Error fetching items:', itemsError)
      return { success: false, error: 'Error fetching items' }
    }

    const allItemsReady = items.every((item) => item.status === 'ready')
    const anyItemPreparing = items.some((item) => item.status === 'preparing')

    console.log('[FULFILLMENT] Items status:', {
      total: items.length,
      allReady: allItemsReady,
      anyPreparing: anyItemPreparing,
    })

    // 3. Check for existing delivery_job
    const { data: deliveryJob } = await supabase
      .from('delivery_jobs')
      .select('id, status, driver_id')
      .eq('order_id', orderId)
      .maybeSingle()

    console.log('[FULFILLMENT] Delivery job:', deliveryJob)

    // 4. Determine new status based on state machine
    let newStatus: OrderStatus = currentStatus

    // STATE MACHINE LOGIC
    if (currentStatus === 'cancelled') {
      console.log('[FULFILLMENT] Order is cancelled, no changes')
      return { success: true, newStatus: currentStatus }
    }

    if (currentStatus === 'delivered') {
      console.log('[FULFILLMENT] Order is delivered, no changes')
      return { success: true, newStatus: currentStatus }
    }

    // Check if items are being prepared
    if (anyItemPreparing && currentStatus !== 'preparing') {
      newStatus = 'preparing'
    }

    // Check if all items are ready
    if (allItemsReady) {
      if (order.delivery_type === 'delivery') {
        // Delivery orders: check delivery job status
        if (!deliveryJob) {
          // Need to create delivery job and search for driver
          newStatus = 'searching_driver'
          await createDeliveryJob(orderId, order.restaurant_id, order)
        } else if (
          DELIVERY_JOB_SEARCHING_STATUSES.includes(deliveryJob.status)
        ) {
          // DB may use 'open', 'pending', or 'searching' — all mean searching_driver
          newStatus = 'searching_driver'
        } else if (
          deliveryJob.status === 'assigned' ||
          deliveryJob.status === 'arrived_pickup'
        ) {
          // Driver accepted but hasn't picked up yet — stay in searching_driver
          newStatus = 'searching_driver'
        } else if (
          deliveryJob.status === 'picked_up' ||
          deliveryJob.status === 'out_for_delivery' ||
          deliveryJob.status === 'en_route'
        ) {
          // Driver has the food — NOW advance to out_for_delivery
          newStatus = 'out_for_delivery'
        } else if (deliveryJob.status === 'delivered') {
          newStatus = 'delivered'
        }
      } else {
        // Pickup or dine-in: just mark as ready
        newStatus = 'ready'
      }
    }

    // 5. Update order status if changed
    if (newStatus !== currentStatus) {
      console.log(
        `[FULFILLMENT] Status change: ${currentStatus} → ${newStatus}`,
      )

      const { error: updateError } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId)

      if (updateError) {
        console.error('[FULFILLMENT] Error updating order:', updateError)
        return { success: false, error: 'Error updating order status' }
      }

      // Create tracking event
      await addTrackingEvent(orderId, order.restaurant_id, newStatus)

      // Create order log
      await addOrderLog(orderId, order.restaurant_id, 'status_change', {
        from: currentStatus,
        to: newStatus,
        trigger: 'fulfillment_recalculation',
      })

      console.log('[FULFILLMENT] ✅ Status updated successfully')
    } else {
      console.log('[FULFILLMENT] No status change needed')
    }

    console.log('[FULFILLMENT] ═══════════════════════════════════')
    return { success: true, newStatus }
  } catch (err: any) {
    console.error('[FULFILLMENT] Exception:', err)
    return { success: false, error: err.message }
  }
}

/**
 * Creates a delivery job for an order.
 * Called when all items are ready and delivery_type is 'delivery'.
 */
async function createDeliveryJob(
  orderId: string,
  restaurantId: string,
  order: any,
): Promise<void> {
  try {
    console.log('[FULFILLMENT] Creating delivery job for order:', orderId)

    // Fetch restaurant details for pickup location
    const { data: restaurant } = await supabase
      .from('restaurants')
      .select('name, address_text, lat, lng')
      .eq('id', restaurantId)
      .single()

    if (!restaurant) {
      console.error('[FULFILLMENT] Restaurant not found')
      return
    }

    // Check if delivery_job already exists (prevent duplicates)
    const { data: existing } = await supabase
      .from('delivery_jobs')
      .select('id')
      .eq('order_id', orderId)
      .maybeSingle()

    if (existing) {
      console.log('[FULFILLMENT] Delivery job already exists, skipping')
      return
    }

    // Create delivery job and get the ID back
    const { data: newJob, error } = await supabase
      .from('delivery_jobs')
      .insert({
        order_id: orderId,
        restaurant_id: restaurantId,
        status: 'open',
        pickup_address: restaurant.address_text || restaurant.name,
        pickup_name: restaurant.name,
        pickup_lat: restaurant.lat,
        pickup_lng: restaurant.lng,
        dropoff_address: order.delivery_address,
        dropoff_name: order.customer_name || 'Cliente',
        dropoff_lat: order.delivery_lat,
        dropoff_lng: order.delivery_lng,
        delivery_fee_estimated: order.delivery_fee || 0,
      })
      .select('id')
      .single()

    if (error || !newJob) {
      console.error('[FULFILLMENT] Error creating delivery job:', error)
      return
    }

    console.log('[FULFILLMENT] ✅ Delivery job created with ID:', newJob.id)

    // Log the creation
    await addOrderLog(orderId, restaurantId, 'delivery_job_created', {
      delivery_job_id: newJob.id,
      pickup: restaurant.name,
      dropoff: order.delivery_address,
    })

    // 🚀 DISPATCH: Send offers to available drivers
    console.log('[FULFILLMENT] 🚀 Dispatching to available drivers...')
    const dispatchResult = await dispatchDeliveryJob(
      newJob.id,
      restaurantId,
      orderId,
    )
    console.log('[FULFILLMENT] Dispatch result:', dispatchResult)
  } catch (err) {
    console.error('[FULFILLMENT] Exception creating delivery job:', err)
  }
}

/**
 * Syncs order status when driver updates delivery job status.
 * Called from driver-hooks after driver actions.
 */
export async function syncOrderFromDeliveryJob(
  deliveryJobId: number,
  newJobStatus: string,
): Promise<void> {
  try {
    console.log('[FULFILLMENT] Syncing order from delivery job:', {
      deliveryJobId,
      newJobStatus,
    })

    // Fetch delivery job to get order_id and driver_id
    const { data: job } = await supabase
      .from('delivery_jobs')
      .select('order_id, restaurant_id, driver_id')
      .eq('id', deliveryJobId)
      .single()

    if (!job) {
      console.error('[FULFILLMENT] Delivery job not found')
      return
    }

    // Map delivery job status to order status
    // CRITICAL: Only advance order status based on ACTUAL driver actions
    // assigned / arrived_pickup = driver accepted but hasn't picked up yet → stay searching_driver
    // picked_up / en_route / out_for_delivery = driver has the food → out_for_delivery
    // delivered = driver completed delivery → delivered
    let orderStatus: OrderStatus | null = null
    let logEventType = 'driver_status_sync'

    switch (newJobStatus) {
      case 'assigned':
        // Driver accepted the offer — order stays in searching_driver phase
        // but we log it and update tracking to show "Conductor asignado"
        orderStatus = 'searching_driver'
        logEventType = 'driver_assigned'
        break
      case 'arrived_pickup':
        // Driver arrived at restaurant — still not picked up
        orderStatus = 'searching_driver'
        logEventType = 'driver_arrived_pickup'
        break
      case 'picked_up':
      case 'en_route':
      case 'out_for_delivery':
        // Driver has the food — NOW we advance to out_for_delivery
        orderStatus = 'out_for_delivery'
        logEventType = 'driver_picked_up'
        break
      case 'delivered':
        // Driver completed delivery
        orderStatus = 'delivered'
        logEventType = 'driver_delivered'
        break
      case 'cancelled':
        orderStatus = 'cancelled'
        logEventType = 'driver_cancelled'
        break
      default:
        console.log(
          '[FULFILLMENT] No order status change needed for:',
          newJobStatus,
        )
        return
    }

    console.log(
      `[FULFILLMENT] Mapping: delivery_job.status=${newJobStatus} → order.status=${orderStatus}`,
    )

    // Fetch current order status to avoid unnecessary updates
    const { data: currentOrder } = await supabase
      .from('orders')
      .select('status')
      .eq('id', job.order_id)
      .single()

    if (currentOrder && currentOrder.status === orderStatus) {
      // Status hasn't changed — still create tracking event for sub-states
      if (newJobStatus === 'assigned') {
        await addTrackingEvent(
          job.order_id,
          job.restaurant_id,
          'searching_driver',
          'Conductor asignado',
          'Un conductor aceptó tu pedido y se dirige al restaurante',
        )
      } else if (newJobStatus === 'arrived_pickup') {
        await addTrackingEvent(
          job.order_id,
          job.restaurant_id,
          'searching_driver',
          'Conductor en restaurante',
          'El conductor llegó al restaurante a recoger tu pedido',
        )
      }
      await addOrderLog(job.order_id, job.restaurant_id, logEventType, {
        delivery_job_status: newJobStatus,
        order_status: orderStatus,
        note: 'order status unchanged, tracking event added',
      })
      console.log(
        '[FULFILLMENT] ✅ Order status unchanged, tracking sub-event added',
      )
      return
    }

    // Build update payload with timestamps and driver_id
    const updatePayload: Record<string, any> = { status: orderStatus }

    // Set driver_id on the order when driver picks up or is assigned
    if (job.driver_id) {
      updatePayload.driver_id = job.driver_id
    }

    // Set picked_up_at when driver picks up the order
    if (
      newJobStatus === 'picked_up' ||
      newJobStatus === 'en_route' ||
      newJobStatus === 'out_for_delivery'
    ) {
      updatePayload.picked_up_at = new Date().toISOString()
    }

    // Set delivered_at when delivery is complete
    if (newJobStatus === 'delivered') {
      updatePayload.delivered_at = new Date().toISOString()
    }

    console.log('[FULFILLMENT] Order update payload:', updatePayload)

    // Update order
    const { error } = await supabase
      .from('orders')
      .update(updatePayload)
      .eq('id', job.order_id)

    if (error) {
      console.error('[FULFILLMENT] Error updating order:', error)
      return
    }

    // Create tracking event with appropriate message
    if (newJobStatus === 'assigned') {
      await addTrackingEvent(
        job.order_id,
        job.restaurant_id,
        'searching_driver',
        'Conductor asignado',
        'Un conductor aceptó tu pedido y se dirige al restaurante',
      )
    } else if (newJobStatus === 'arrived_pickup') {
      await addTrackingEvent(
        job.order_id,
        job.restaurant_id,
        'searching_driver',
        'Conductor en restaurante',
        'El conductor llegó al restaurante a recoger tu pedido',
      )
    } else {
      await addTrackingEvent(job.order_id, job.restaurant_id, orderStatus)
    }

    // Create order log
    await addOrderLog(job.order_id, job.restaurant_id, logEventType, {
      delivery_job_status: newJobStatus,
      order_status: orderStatus,
    })

    console.log('[FULFILLMENT] ✅ Order synced from delivery job')
  } catch (err) {
    console.error('[FULFILLMENT] Exception syncing order:', err)
  }
}
